import {
	isAfterLocalDay,
	isBeforeLocalDay,
} from './dateUtils';

/**
 * Checks if the current time is outside a recurring time window.
 * Times are stored as UTC HH:MM:SS format.
 *
 * @param {Date} now - The current date/time
 * @param {string} startTimeStr - Start time in "HH:MM:SS" format (UTC)
 * @param {string} endTimeStr - End time in "HH:MM:SS" format (UTC)
 * @returns {boolean} - Returns true if outside the time window (should skip), false if within
 */
export const isOutsideTimeWindow = (now, startTimeStr, endTimeStr) => {
	const [ startHours, startMinutes, startSeconds, ] = (startTimeStr || '00:00:00').split(':').map(Number);
	const [ endHours, endMinutes, endSeconds, ] = (endTimeStr || '23:59:59').split(':').map(Number);

	// Create Date objects with today's UTC date and the UTC times
	const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), startHours, startMinutes, startSeconds || 0, 0));
	const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), endHours, endMinutes, endSeconds || 0, 999));

	// Check if the time window wraps across midnight UTC
	const wrapsToNextDay = todayEnd <= todayStart;

	if (wrapsToNextDay) {
		// For wrap-around windows (e.g., 16:00 UTC to 00:30 UTC next day),
		// we need to check two possible windows:
		// 1. Yesterday's start to today's end (for the early morning hours)
		// 2. Today's start to tomorrow's end (for the evening hours)

		const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
		const tomorrowEnd = new Date(todayEnd.getTime() + 24 * 60 * 60 * 1000);

		// Check if we're in yesterday's window (still ongoing) OR today's window
		const inYesterdayWindow = now >= yesterdayStart && now <= todayEnd;
		const inTodayWindow = now >= todayStart && now <= tomorrowEnd;

		return !inYesterdayWindow && !inTodayWindow;
	}

	// Non-wrapping window: simple check
	return now < todayStart || now > todayEnd;
};

/**
 * Determines if a slide should be skipped based on its scheduling configuration.
 * Handles date/time ranges, day-of-week filtering, and recurring times with UTC conversion.
 *
 * When `slide.folder_slides` is an array, scheduling is borrowed from the first
 * child slide (folders share one schedule) and an empty folder is always skipped.
 *
 * @param {Object} slide - The slide object with optional scheduling configuration
 * @param {Date} [nowOverride] - Date to evaluate against; defaults to real now.
 *   For a non-today VCL loop date this is local noon on that date
 *   (see `getNowForLoopDate` in dateUtils).
 * @param {boolean} [skipTimeChecks=false] - Drop every time-of-day consideration.
 *   Used when previewing a non-today loop date, where there is no meaningful
 *   "current time" — a slide scheduled 7 AM-2 PM must not vanish from a future-day
 *   preview because the wall clock happens to read 3 PM today. Two effects:
 *   1. `recurring_start_time`/`recurring_end_time` and the `recurring_times_by_day`
 *      start/end window are not evaluated at all.
 *   2. `start_date`/`end_date` are compared by CALENDAR DAY, not by instant — so on
 *      a slide's first or last day it is in or out for the whole day.
 *   (2) matters because `now` is then a synthesized local noon (`getNowForLoopDate`),
 *   an arbitrary instant with no relationship to the stored one: comparing it against
 *   `2026-08-15T18:00:00Z` would hide the slide for the whole of the 15th purely
 *   because noon precedes 18:00. Date-only is also what the folder path already does
 *   (`folder_file_date_ranges` are compared as `yyyy-mm-dd` strings) and what the
 *   ticket asks for ("no need to accommodate times").
 *   Day-of-week and "this day has no schedule entry" still apply, so the preview
 *   never shows content that is genuinely unscheduled for that date.
 * @returns {boolean} - Returns true if the slide should be skipped, false if it should be shown
 */
export const shouldSkipSlide = (slide, nowOverride = null, skipTimeChecks = false) => {
	const now = nowOverride || new Date();
	let scheduling = slide.scheduling;
	let scheduled = slide.scheduled;

	if (Array.isArray(slide.folder_slides)) {
		if (slide.folder_slides.length === 0) {
			return true; // skip empty folders
		}

		// folders share scheduling for all slides; the folder parent itself
		// carries no toggle, so read it from the same place as the rules
		scheduling = slide.folder_slides[0].scheduling;
		scheduled = slide.folder_slides[0].scheduled;
	}

	// scheduled === false is the deck row's scheduling toggle turned OFF —
	// the slide always plays, regardless of stale rules left in the
	// scheduling JSON (keep in sync with condor's util/slides.js)
	if (scheduled === false) {
		return false;
	}

	if (!scheduling) {
		return false;
	}

	// Check date range (start_date and end_date are in UTC ISO format).
	// When previewing another day, `now` is a synthesized local noon, so comparing it
	// against a stored instant is meaningless — the range is compared by calendar day
	// and a slide's first/last day is in or out for the whole day. See the JSDoc.
	if (scheduling.start_date) {
		const startDate = new Date(scheduling.start_date);
		if (skipTimeChecks ? isBeforeLocalDay(now, startDate) : now < startDate) {
			return true; // Skip if before start date
		}
	}

	if (scheduling.end_date) {
		const endDate = new Date(scheduling.end_date);
		if (skipTimeChecks ? isAfterLocalDay(now, endDate) : now > endDate) {
			return true; // Skip if after end date
		}
	}

	// Check recurring times (recurring_start_time and recurring_end_time are stored as UTC HH:MM:SS format)
	if (!skipTimeChecks && (scheduling.recurring_start_time || scheduling.recurring_end_time)) {
		if (isOutsideTimeWindow(now, scheduling.recurring_start_time, scheduling.recurring_end_time)) {
			return true; // Skip if outside the time window
		}
	}

	// Check day of week
	if (scheduling.days && Array.isArray(scheduling.days) && scheduling.days.length > 0) {
		const today = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
		if (!scheduling.days.includes(today)) {
			return true; // Skip if today is not in the allowed days
		}
	}

	// Check recurring times by day
	if (scheduling.recurring_times_by_day && Object.keys(scheduling.recurring_times_by_day).length > 0) {
		const today = now.getDay();
		// Keys from JSON are strings, so check both number and string keys
		const todaySchedule = scheduling.recurring_times_by_day[today] || scheduling.recurring_times_by_day[String(today)];

		// If today is not in the schedule, skip
		if (!todaySchedule) {
			return true;
		}

		// If use_recurring_times is false, the slide shows all day for this day (no time check needed)
		if (todaySchedule.use_recurring_times === false) {
			return false; // Don't skip - show all day
		}

		// If use_recurring_times is true, check the time window
		if (!skipTimeChecks && todaySchedule.use_recurring_times && (todaySchedule.start_time || todaySchedule.end_time)) {
			if (isOutsideTimeWindow(now, todaySchedule.start_time, todaySchedule.end_time)) {
				return true; // Skip if outside the time window
			}
		}
	}

	return false;
};

/**
 * gallery_file.order has two "default" populations: 0 (DB column default, raw
 * uploads) and 1 (Library form default). Both mean "unordered" — collapse them
 * so untouched files sort together and the UI matches the Library's displayed
 * default of 1.
 *
 * @param {*} order
 * @returns {number}
 */
export function normalizeOrder(order) {
	const n = Number(order) || 0;
	return n < 1 ? 1 : n;
}

/**
 * Default sorter for slides/folder files.
 * Matches bronco's folder-file playback sort in makeProviderFromSlideDeck:
 * normalized `order` (0/1 both "unordered"), then alphabetically by
 * name/description, then by ID.
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
export function defaultSorter(a, b) {
	let orderA = a.order ?? a?.data?.order ?? a?.props?.order ?? a?.options?.providerOrder ?? 0;
	let orderB = b.order ?? b?.data?.order ?? b?.props?.order ?? b?.options?.providerOrder ?? 0;

	orderA = normalizeOrder(orderA);
	orderB = normalizeOrder(orderB);

	if (orderA !== orderB) {
		return orderA - orderB;
	}

	const nameA = (a?.data?.description || a?.description || a?.title || a?.name || a?.file_name || '').toLowerCase();
	const nameB = (b?.data?.description || b?.description || b?.title || b?.name || b?.file_name || '').toLowerCase();

	if (nameA !== nameB) {
		return nameA < nameB ? -1 : 1;
	}

	const idA = String(a.providerID || a?.props?.component_name || a?.id || '');
	const idB = String(b.providerID || b?.props?.component_name || b?.id || '');

	return idA < idB ? -1 : idA > idB ? 1 : 0;
}

// ---------------------------------------------------------------------------
// URL (kind 'html') slides — DEV-12028
// ---------------------------------------------------------------------------

// Generic icon shown when a URL slide's site has no favicon (or the URL is unparseable).
export const urlSlideFallbackIcon = 'https://ts-public-images.s3.us-east-1.amazonaws.com/url-icon.svg';

// `slide.file` is varchar(255) NOT NULL in bronco — longer URLs fail the INSERT under
// STRICT_TRANS_TABLES, so the modal must refuse them up front.
export const URL_SLIDE_MAX_LENGTH = 255;

// URL slides have no S3 asset: use the site's favicon as the thumbnail.
export function getURLSlideThumbnailPath(file) {
	try {
		return `${new URL(file).origin}/favicon.ico`;
	} catch (e) {
		return urlSlideFallbackIcon;
	}
}

// A URL slide must be an absolute https URL with a host, short enough for slide.file.
// (Rendered in a sandboxed iframe on the kiosk, so plain http is refused.)
export function isValidURLSlideURL(value) {
	const url = (value || '').trim();
	if (!url || url.length > URL_SLIDE_MAX_LENGTH) {
		return false;
	}
	try {
		const parsed = new URL(url);
		// Reject embedded credentials: `https://user:pass@host/` would persist
		// secrets in slide.file, and `https://good.com@evil.com/` is a spoofing
		// vector (the visible "host" before @ is really the username).
		if (parsed.username || parsed.password) {
			return false;
		}
		return parsed.protocol === 'https:' && !!parsed.hostname;
	} catch (e) {
		return false;
	}
}
